import { findAllConstructionTypesFromObjects } from 'components/WorkOrderConstructionTypesTabSelect';
import type { BimWorkOrderObjectData, ConstructionTypeData } from 'gql/graphql';
import { BimChapterEnum } from 'gql/graphql';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useScrollToWithNavigationInfo } from 'hooks/useScrollToWithNavigationInfo';
import type { IListItem } from 'types/Contract';

import { useCallback } from 'react';

import { bimWorkOrderChaptersAndSectionsListItems } from '../types';

type Props = {
    bimWorkOrderObjects: BimWorkOrderObjectData[] | undefined | null;
    handleConstructionTypeChange: (type: ConstructionTypeData) => void;
    onRootObjectChange: (rootObject: IListItem<number>) => void;
    onSectionOrChapterChange: (sectionOrChapter: IListItem<string>) => void;
    priceTableApiRef: any;
};

export const useBimScrollToAct = ({
    bimWorkOrderObjects,
    handleConstructionTypeChange,
    onRootObjectChange,
    onSectionOrChapterChange,
    priceTableApiRef,
}: Props) => {
    const localTypeSearcher = useCallback(
        (id: number) => {
            const constructionTypes = findAllConstructionTypesFromObjects({
                workOrderChangeObjectsData: bimWorkOrderObjects ?? [],
            });

            const type = constructionTypes.find((localType) => localType.id === id);

            if (type) {
                handleConstructionTypeChange(type);
            }
        },
        [bimWorkOrderObjects, handleConstructionTypeChange],
    );

    const { handleSaveFilterState } = usePageFilterSaver({});

    const localFilterSetter = useCallback(
        ({ objectId }: { objectId: number }, justForSaveConstructionTypeId?: number) => {
            const otherChapter = bimWorkOrderChaptersAndSectionsListItems.find((i) => i.id === BimChapterEnum.Others);

            const newObject = bimWorkOrderObjects?.find((i) => i.workOrderObjectId === objectId);

            if (otherChapter && newObject) {
                onSectionOrChapterChange(otherChapter);
                setTimeout(() => {
                    onRootObjectChange({
                        id: newObject.id,
                        workOrderObjectId: newObject.workOrderObjectId,
                        label: newObject.name,
                    });
                }, 0);

                handleSaveFilterState({
                    key: String(justForSaveConstructionTypeId),
                    newState: {
                        chapter: otherChapter,
                        object: {
                            id: newObject.id,
                            workOrderObjectId: newObject.workOrderObjectId,
                            label: newObject.name,
                        },
                    },
                });
            }
        },
        [bimWorkOrderObjects, handleSaveFilterState, onRootObjectChange, onSectionOrChapterChange],
    );

    useScrollToWithNavigationInfo({
        constructionTypeSetter: localTypeSearcher,
        objectSetter: localFilterSetter,
        objects: bimWorkOrderObjects,
        tableApi: priceTableApiRef?.current?.current,
        expandColumnsHandler: priceTableApiRef?.current?.api?.outerExpandOpener,
    });
};
